import {computed, onMounted, ref, toValue} from 'vue';

/**
 * useJoinRequestStatus — reads the current user's pending join requests straight
 * off the auth `user` object. UserDetailResponse exposes club/ladder/tournament/
 * leagueCycleApplications as [{ id, entityId }] (id = accessApplication id,
 * entityId = the entity's id). Lets every join card show a "requested" state and
 * cancel without an extra fetch on page load.
 *
 * 'meet' and 'americanoCycle' have no user-object array (UserDetailResponse
 * carries no meetApplications/americanoCycleApplications), so they fall back to
 * a one-off client fetch of the user's own accessApplications scoped to the
 * meet / americano cycle.
 *
 * @param {'club'|'ladder'|'tournament'|'leagueCycle'|'meet'|'americanoCycle'} entityType
 * @param {Ref|Function|string} entityId  the entity's id (ref/getter/plain)
 */
export function useJoinRequestStatus(entityType, entityId) {
  const {user, updateLocalUser, isAuthenticated} = useAuthService();
  const {notify} = useListenerService();
  const {deleteItem, getItems, items: fetchedApplications} = useCrudApi('api/v2/accessApplications');

  const arrayKey = {
    club: 'clubApplications',
    ladder: 'ladderApplications',
    tournament: 'tournamentApplications',
    leagueCycle: 'leagueCycleApplications',
  }[entityType];

  // Meet fallback: fetch my own pending application rows client-side.
  const meetApplication = ref(null);
  const loadMeetApplication = async () => {
    const id = toValue(entityId);
    if (entityType !== 'meet' || !id || !isAuthenticated.value || !user.value?.id) return;
    await getItems(1, 1, {meetIds: [id], userIds: [user.value.id]});
    meetApplication.value = (fetchedApplications.value ?? []).find((row) => !row.deletedAt) ?? null;
  };
  if (entityType === 'meet') {
    onMounted(loadMeetApplication);
  }

  // Americano-cycle fallback: same pattern as meets — fetch my own pending rows.
  const americanoCycleApplication = ref(null);
  const loadAmericanoCycleApplication = async () => {
    const id = toValue(entityId);
    if (entityType !== 'americanoCycle' || !id || !isAuthenticated.value || !user.value?.id) return;
    await getItems(1, 1, {americanoCycleIds: [id], userIds: [user.value.id]});
    americanoCycleApplication.value = (fetchedApplications.value ?? []).find((row) => !row.deletedAt) ?? null;
  };
  if (entityType === 'americanoCycle') {
    onMounted(loadAmericanoCycleApplication);
  }

  // The matching pending entry ({ id, entityId }) or null.
  const pendingApplication = computed(() => {
    const id = toValue(entityId);
    if (!id) return null;
    if (entityType === 'meet') {
      return meetApplication.value?.meetId === id ? meetApplication.value : null;
    }
    if (entityType === 'americanoCycle') {
      return americanoCycleApplication.value?.americanoCycleId === id ? americanoCycleApplication.value : null;
    }
    if (!arrayKey) return null;
    return (user.value?.[arrayKey] ?? []).find((a) => a.entityId === id) ?? null;
  });

  const isPending = computed(() => !!pendingApplication.value);

  const cancelling = ref(false);
  const cancelRequest = async () => {
    const application = pendingApplication.value;
    if (!application) return;
    cancelling.value = true;
    try {
      await deleteItem(application.id);
      if (entityType === 'meet') {
        meetApplication.value = null;
      } else if (entityType === 'americanoCycle') {
        americanoCycleApplication.value = null;
      } else {
        await updateLocalUser(); // refresh the pending arrays from /auth/me
      }
      notify('requestsUpdated', null);
    } finally {
      cancelling.value = false;
    }
  };

  const refresh = entityType === 'americanoCycle' ? loadAmericanoCycleApplication : loadMeetApplication;

  return {pendingApplication, isPending, cancelling, cancelRequest, refresh};
}
